import { Monitor, Moon, Sun } from 'lucide-react';
import { useTheme } from 'next-themes';
import { useLanguage } from '@/contexts/language-context';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const choices = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'System', icon: Monitor },
] as const;

export function ThemeSwitcher() {
  const { theme = 'system', setTheme } = useTheme();
  const { t, direction } = useLanguage();
  const current = choices.find((choice) => choice.value === theme) ?? choices[2];
  const Icon = current.icon;

  return (
    <DropdownMenu dir={direction}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="button-theme-switcher"
          aria-label={t('Appearance: {theme}', { theme: t(current.label) })}
          title={t('Change theme')}
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-sm text-foreground transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <Icon aria-hidden="true" className="h-5 w-5 stroke-[1.5]" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-40 border-border bg-background text-foreground">
        <DropdownMenuLabel>{t('Appearance')}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup value={current.value} onValueChange={setTheme}>
          {choices.map(({ value, label, icon: ChoiceIcon }) => (
            <DropdownMenuRadioItem
              key={value}
              value={value}
              data-testid={`theme-option-${value}`}
              className="gap-2 focus:bg-muted focus:text-foreground"
            >
              <ChoiceIcon aria-hidden="true" className="h-4 w-4" />
              {t(label)}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
